import React, { useState } from 'react';
import styled from 'styled-components';
import { DollarSign, Plus, Edit, Trash2, Search, TrendingDown, Package, FileText, Calculator } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.border};
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  background: transparent;
  border-bottom: 3px solid ${({ active }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.base};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const SummaryCard = styled(Card)`
  padding: ${theme.spacing.xl};
  text-align: center;
  
  h3 {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.regular};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${({ type }) => type === 'expense' ? theme.colors.status.outOfStock : theme.colors.primary};
  }
`;

const TableContainer = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${theme.colors.secondary};
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${theme.colors.border};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semiBold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.secondary};
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

// Mock data
const initialExpenses = [
  { id: 1, category: 'Rent', amount: 50000, date: '2024-12-01', description: 'Monthly rent', supplier: 'Property Owner' },
  { id: 2, category: 'Utilities', amount: 15000, date: '2024-12-05', description: 'Electricity bill', supplier: 'Power Company' },
  { id: 3, category: 'Ingredients', amount: 25000, date: '2024-12-10', description: 'Monthly groceries', supplier: 'Food Supplier Co.' },
];

const initialSuppliers = [
  { id: 1, name: 'Food Supplier Co.', contact: '+91 9876543210', email: 'contact@foodsupplier.com', items: 'Vegetables, Spices', status: 'active' },
  { id: 2, name: 'Beverage Distributors', contact: '+91 9876543211', email: 'sales@beverage.com', items: 'Soft Drinks, Juices', status: 'active' },
];

const initialPurchaseOrders = [
  { id: 1, poNumber: 'PO-001', supplier: 'Food Supplier Co.', items: 'Rice, Flour, Oil', total: 15000, status: 'pending', date: '2024-12-20' },
  { id: 2, poNumber: 'PO-002', supplier: 'Beverage Distributors', items: 'Coca Cola, Pepsi', total: 5000, status: 'approved', date: '2024-12-18' },
];

function FinancialManagement() {
  const [activeTab, setActiveTab] = useState('expenses');
  const [expenses, setExpenses] = useState(initialExpenses);
  const [suppliers, setSuppliers] = useState(initialSuppliers);
  const [purchaseOrders, setPurchaseOrders] = useState(initialPurchaseOrders);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalRevenue = 250000; // Mock revenue
  const profit = totalRevenue - totalExpenses;
  const profitMargin = ((profit / totalRevenue) * 100).toFixed(2);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (activeTab === 'expenses') {
        setExpenses((prev) => prev.filter((item) => item.id !== id));
      } else if (activeTab === 'suppliers') {
        setSuppliers((prev) => prev.filter((item) => item.id !== id));
      } else {
        setPurchaseOrders((prev) => prev.filter((item) => item.id !== id));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'expenses') {
      if (editingItem) {
        setExpenses((prev) => prev.map((item) => (item.id === editingItem.id ? { ...item, ...formData, amount: parseFloat(formData.amount) } : item)));
      } else {
        setExpenses((prev) => [...prev, { id: Date.now(), ...formData, amount: parseFloat(formData.amount) }]);
      }
    } else if (activeTab === 'suppliers') {
      if (editingItem) {
        setSuppliers((prev) => prev.map((item) => (item.id === editingItem.id ? { ...item, ...formData } : item)));
      } else {
        setSuppliers((prev) => [...prev, { id: Date.now(), ...formData }]);
      }
    } else {
      if (editingItem) {
        setPurchaseOrders((prev) => prev.map((item) => (item.id === editingItem.id ? { ...item, ...formData, total: parseFloat(formData.total) } : item)));
      } else {
        setPurchaseOrders((prev) => [...prev, { id: Date.now(), poNumber: `PO-${String(prev.length + 1).padStart(3, '0')}`, ...formData, total: parseFloat(formData.total) }]);
      }
    }
    setShowModal(false);
  };

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Financial Management</h1>
        </Header>

        <SummaryCards>
          <SummaryCard>
            <h3>Total Revenue</h3>
            <p>₹{totalRevenue.toLocaleString()}</p>
          </SummaryCard>
          <SummaryCard type="expense">
            <h3>Total Expenses</h3>
            <p>₹{totalExpenses.toLocaleString()}</p>
          </SummaryCard>
          <SummaryCard>
            <h3>Net Profit</h3>
            <p>₹{profit.toLocaleString()}</p>
          </SummaryCard>
          <SummaryCard>
            <h3>Profit Margin</h3>
            <p>{profitMargin}%</p>
          </SummaryCard>
        </SummaryCards>

        <Tabs>
          <Tab active={activeTab === 'expenses'} onClick={() => setActiveTab('expenses')}>
            <TrendingDown size={18} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
            Expenses
          </Tab>
          <Tab active={activeTab === 'suppliers'} onClick={() => setActiveTab('suppliers')}>
            <Package size={18} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
            Suppliers
          </Tab>
          <Tab active={activeTab === 'purchaseOrders'} onClick={() => setActiveTab('purchaseOrders')}>
            <FileText size={18} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
            Purchase Orders
          </Tab>
          <Tab active={activeTab === 'costAnalysis'} onClick={() => setActiveTab('costAnalysis')}>
            <Calculator size={18} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
            Cost Analysis
          </Tab>
        </Tabs>

        {activeTab === 'expenses' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing.lg }}>
              <Button variant="primary" onClick={handleAdd}>
                <Plus size={20} />
                Add Expense
              </Button>
            </div>
            <TableContainer>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Category</TableHeaderCell>
                    <TableHeaderCell>Description</TableHeaderCell>
                    <TableHeaderCell>Supplier</TableHeaderCell>
                    <TableHeaderCell>Amount</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell>{expense.supplier}</TableCell>
                      <TableCell>₹{expense.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <ActionButtons>
                          <ActionButton onClick={() => handleEdit(expense)}>
                            <Edit /> Edit
                          </ActionButton>
                          <ActionButton onClick={() => handleDelete(expense.id)}>
                            <Trash2 /> Delete
                          </ActionButton>
                        </ActionButtons>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {activeTab === 'suppliers' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing.lg }}>
              <Button variant="primary" onClick={handleAdd}>
                <Plus size={20} />
                Add Supplier
              </Button>
            </div>
            <TableContainer>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Contact</TableHeaderCell>
                    <TableHeaderCell>Email</TableHeaderCell>
                    <TableHeaderCell>Items</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell><strong>{supplier.name}</strong></TableCell>
                      <TableCell>{supplier.contact}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>{supplier.items}</TableCell>
                      <TableCell>{supplier.status}</TableCell>
                      <TableCell>
                        <ActionButtons>
                          <ActionButton onClick={() => handleEdit(supplier)}>
                            <Edit /> Edit
                          </ActionButton>
                          <ActionButton onClick={() => handleDelete(supplier.id)}>
                            <Trash2 /> Delete
                          </ActionButton>
                        </ActionButtons>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {activeTab === 'purchaseOrders' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing.lg }}>
              <Button variant="primary" onClick={handleAdd}>
                <Plus size={20} />
                Create Purchase Order
              </Button>
            </div>
            <TableContainer>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>PO Number</TableHeaderCell>
                    <TableHeaderCell>Supplier</TableHeaderCell>
                    <TableHeaderCell>Items</TableHeaderCell>
                    <TableHeaderCell>Total</TableHeaderCell>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {purchaseOrders.map((po) => (
                    <TableRow key={po.id}>
                      <TableCell><strong>{po.poNumber}</strong></TableCell>
                      <TableCell>{po.supplier}</TableCell>
                      <TableCell>{po.items}</TableCell>
                      <TableCell>₹{po.total.toLocaleString()}</TableCell>
                      <TableCell>{new Date(po.date).toLocaleDateString()}</TableCell>
                      <TableCell>{po.status}</TableCell>
                      <TableCell>
                        <ActionButtons>
                          <ActionButton onClick={() => handleEdit(po)}>
                            <Edit /> Edit
                          </ActionButton>
                        </ActionButtons>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {activeTab === 'costAnalysis' && (
          <Card>
            <h2 style={{ marginBottom: theme.spacing.lg }}>Cost Analysis & Profit Margin</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: theme.spacing.lg }}>
              <div>
                <h3>Revenue Breakdown</h3>
                <p>Food Sales: ₹200,000</p>
                <p>Beverage Sales: ₹50,000</p>
              </div>
              <div>
                <h3>Expense Breakdown</h3>
                <p>Rent: ₹50,000</p>
                <p>Utilities: ₹15,000</p>
                <p>Ingredients: ₹25,000</p>
                <p>Other: ₹10,000</p>
              </div>
              <div>
                <h3>Profit Analysis</h3>
                <p>Gross Profit: ₹{profit.toLocaleString()}</p>
                <p>Profit Margin: {profitMargin}%</p>
                <p>Break-even Point: ₹{totalExpenses.toLocaleString()}</p>
              </div>
            </div>
          </Card>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>
                  {activeTab === 'expenses' && (editingItem ? 'Edit Expense' : 'Add Expense')}
                  {activeTab === 'suppliers' && (editingItem ? 'Edit Supplier' : 'Add Supplier')}
                  {activeTab === 'purchaseOrders' && (editingItem ? 'Edit Purchase Order' : 'Create Purchase Order')}
                </h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                {activeTab === 'expenses' && (
                  <>
                    <InputGroup>
                      <label>Date</label>
                      <Input type="date" value={formData.date || ''} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Category</label>
                      <select
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem 1rem', fontSize: theme.typography.fontSize.base, fontFamily: theme.typography.fontFamily, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}
                        required
                      >
                        <option value="">Select category...</option>
                        <option value="Rent">Rent</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Ingredients">Ingredients</option>
                        <option value="Salaries">Salaries</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Other">Other</option>
                      </select>
                    </InputGroup>
                    <InputGroup>
                      <label>Description</label>
                      <Input type="text" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Supplier</label>
                      <Input type="text" value={formData.supplier || ''} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} />
                    </InputGroup>
                    <InputGroup>
                      <label>Amount (₹)</label>
                      <Input type="number" step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                    </InputGroup>
                  </>
                )}
                {activeTab === 'suppliers' && (
                  <>
                    <InputGroup>
                      <label>Supplier Name</label>
                      <Input type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Contact</label>
                      <Input type="tel" value={formData.contact || ''} onChange={(e) => setFormData({ ...formData, contact: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Email</label>
                      <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Items Supplied</label>
                      <Input type="text" value={formData.items || ''} onChange={(e) => setFormData({ ...formData, items: e.target.value })} required />
                    </InputGroup>
                  </>
                )}
                {activeTab === 'purchaseOrders' && (
                  <>
                    <InputGroup>
                      <label>Supplier</label>
                      <select
                        value={formData.supplier || ''}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem 1rem', fontSize: theme.typography.fontSize.base, fontFamily: theme.typography.fontFamily, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}
                        required
                      >
                        <option value="">Select supplier...</option>
                        {suppliers.map((s) => (
                          <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </InputGroup>
                    <InputGroup>
                      <label>Items</label>
                      <Input type="text" value={formData.items || ''} onChange={(e) => setFormData({ ...formData, items: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Total Amount (₹)</label>
                      <Input type="number" step="0.01" value={formData.total || ''} onChange={(e) => setFormData({ ...formData, total: e.target.value })} required />
                    </InputGroup>
                    <InputGroup>
                      <label>Date</label>
                      <Input type="date" value={formData.date || ''} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                    </InputGroup>
                  </>
                )}
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingItem ? 'Update' : 'Add'}
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default FinancialManagement;

